import { useTranslation } from 'react-i18next'
import { useState, useRef, useEffect } from 'react'
import { useWindowActions } from 'models/window'
import { formatNumbers } from 'core/helpers/format-numbers'
import { gameService, userService } from '../../../core/services'
import { useAuthContext } from '../../../core/providers/AuthProvider'
import { toast } from 'react-toastify'
import { getPromoUrl, PromoUrlType } from '../../../core/helpers/promo-url'
import { useAutoTranslate } from '../../../core/hooks/useAutoTranslate'

import c from './assets/sass/caseModal.module.scss'

export const CaseModal = ({ caseData, onClaimed }) => {
    const { t } = useTranslation()
    const { closeWindow } = useWindowActions()
    const { updateUserData } = useAuthContext()
    const [code, setCode] = useState(null)
    const [isBought, setIsBought] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [caseInfo, setCaseInfo] = useState({
        name: 'Фартовый',
        price: 9000,
        id: null,
        imageUrl: '/images/case-01.png',
        category: 'STANDOFF_2',
    })
    const [isCopied, setIsCopied] = useState(false)
    const [canClose, setCanClose] = useState(true)
    
    // Автоперевод названия кейса
    const translatedCaseName = useAutoTranslate(caseInfo?.name)

    useEffect(() => {
        ;(async () => {
            try {
                if (caseData && (caseData.id || (caseData.isHistory && caseData.promoCode))) {
                    setCaseInfo({
                        id: caseData.id || null,
                        caseId: caseData.caseId || caseData.id || null,
                        name: caseData.name,
                        price: parseInt(caseData.price) || 0,
                        imageUrl: caseData.imageUrl,
                        category: caseData.category || 'STANDOFF_2',
                        codes: caseData.codes,
                        isHistory: caseData.isHistory || false,
                        selectedGame: caseData.selectedGame || null,
                    })
                    if (caseData.isHistory && caseData.promoCode) {
                        setCode(caseData.promoCode)
                        setIsBought(true)
                        setCanClose(false)
                    }
                } else {
                    const res = await gameService.cases.getCases()
                    const first = res?.cases?.[0]
                    if (first)
                        setCaseInfo({
                            id: first.id,
                            caseId: first.id,
                            name: first.name,
                            price: parseInt(first.price),
                            imageUrl: first.imageUrl,
                            category: first.category || 'STANDOFF_2',
                            codes: first.codes,
                            isHistory: false,
                        })
                }
            } catch {}
        })()
    }, [caseData])

    const [translateY, setTranslateY] = useState(0)
    const startY = useRef(0)
    const dragging = useRef(false)

    const handleDragStart = (e) => {
        dragging.current = true
        startY.current = e.touches ? e.touches[0].clientY : e.clientY
    }

    const handleDragMove = (e) => {
        if (!dragging.current) return
        const currentY = e.touches ? e.touches[0].clientY : e.clientY
        const diff = currentY - startY.current
        if (diff > 0) {
            setTranslateY(diff)
        }
    }

    const handleDragEnd = () => {
        if (!dragging.current) return
        dragging.current = false

        if (translateY > 50) {
            if (canClose) {
                closeWindow()
            } else {
                toast(t('profile.cases.error.copyBeforeClose'), {
                    type: 'warning',
                })
                setTranslateY(0)
            }
        } else {
            setTranslateY(0)
        }
    }

    const handleCopyCode = () => {
        if (!code) return
        navigator.clipboard
            .writeText(code)
            .then(() => {
                toast(t('profile.cases.error.codeCopied'), { type: 'success' })
                setIsCopied(true)
                setCanClose(true)
                setTimeout(() => setIsCopied(false), 2000)
            })
            .catch(() => {
                toast(t('profile.cases.error.copyError'), { type: 'error' })
            })
    }

    const handleGoToSite = async () => {
        console.log('🔗 handleGoToSite called', {
            isCopied,
            code,
            isHistory: caseInfo.isHistory,
        })

        // УБРАЛИ проверку isCopied - юзер может сразу перейти на сайт
        // Промокод автоматически вставлен в URL

        if (caseInfo.isHistory && caseInfo.id) {
            try {
                await userService.markCaseViewed(caseInfo.id)
                console.log('✅ Case marked as viewed:', caseInfo.id)

                // Вызываем callback для обновления списка
                if (onClaimed) {
                    onClaimed()
                }
            } catch (error) {
                console.error('Failed to mark case as viewed:', error)
            }
        }

        // Генерируем URL с промокодом (кейсы - это bonus_promo_code)
        const url = getPromoUrl(code, PromoUrlType.CASE)
        console.log('🔗 Generated promo URL:', url)

        // Пробуем разные способы открыть ссылку
        try {
            if (window?.Telegram?.WebApp?.openLink) {
                console.log('🔗 Using Telegram.WebApp.openLink')
                window.Telegram.WebApp.openLink(url)
            } else if (window?.Telegram?.WebApp?.openTelegramLink) {
                // Для некоторых версий
                console.log('🔗 Using Telegram.WebApp.openTelegramLink')
                window.Telegram.WebApp.openTelegramLink(url)
            } else {
                console.log('🔗 Using window.open')
                window.open(url, '_blank')
            }
        } catch (e) {
            console.error('🔗 Error opening link:', e)
            // Fallback - просто открываем через window.open
            window.open(url, '_blank')
        }

        // Автоматически разрешаем закрытие после перехода
        setCanClose(true)
    }

    const handleConfirmBuy = async () => {
        try {
            if (!caseInfo?.id) return
            setShowConfirm(false)
            setIsLoading(true)
            const resp = await gameService.cases.openCase(caseInfo.id, caseInfo.selectedGame)
            if (resp?.promoCode) {
                setCode(resp.promoCode)
                setIsBought(true)
                await updateUserData()
            }
        } catch (e) {
            const code = e?.response?.data?.code
            if (code === 'INSUFFICIENT_FUNDS') {
                toast(t('profile.cases.error.insufficient'), { type: 'error' })
            } else if (code === 'CODES_DEPLETED') {
                toast(t('profile.cases.error.noCodes'), { type: 'error' })
            } else if (code === 'DAILY_LIMIT_REACHED') {
                toast(t('profile.cases.error.dailyLimit'), { type: 'error' })
            } else {
                toast(t('profile.cases.error.buyError'), { type: 'error' })
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleBuyClick = () => {
        if (!caseInfo?.id) return
        setShowConfirm(true)
    }

    // Автоперевод для confirm блока
    const confirmTitle = useAutoTranslate('Подтверждение покупки')
    const confirmQuestion1 = useAutoTranslate('Вы уверены что хотите купить кейс')
    const confirmQuestion2 = useAutoTranslate('за')
    const confirmQuestion3 = useAutoTranslate('монет?')
    const confirmTextStandoff = useAutoTranslate('Учтите он будет работать только в разделе игры Standoff 2')
    const confirmTextPubg = useAutoTranslate('Учтите он будет работать только в разделе игры PUBG MOBILE')
    const confirmYes = useAutoTranslate('Да')
    const confirmNo = useAutoTranslate('Нет')

    const getCategoryWarning = () => {
        const game = caseInfo.selectedGame || caseInfo.category
        if (game === 'PUBG_MOBILE') {
            return confirmTextPubg
        }
        return confirmTextStandoff
    }

    const getBottom = () => {
        // Показываем confirm до покупки
        if (!isBought && showConfirm) {
            return (
                <div className={c.bottom}>
                    <div className={c.confirmBlock}>
                        <p className={c.confirmTitle}>{confirmTitle}</p>
                        <p className={c.confirmText}>
                            {confirmQuestion1} <strong>{translatedCaseName}</strong> {confirmQuestion2} {formatNumbers(caseInfo.price)} {confirmQuestion3}
                        </p>
                        <p className={c.confirmWarning}>
                            {getCategoryWarning()}
                        </p>
                    </div>
                    <button
                        className={c.get}
                        onClick={handleConfirmBuy}
                        disabled={isLoading}
                    >
                        <span>{confirmYes}</span>
                    </button>
                    <button
                        className={c.cancel}
                        onClick={() => setShowConfirm(false)}
                        disabled={isLoading}
                    >
                        <span>{confirmNo}</span>
                    </button>
                </div>
            )
        }

        if (!isBought) {
            return (
                <div className={c.bottom}>
                    <span className={c.note}>{t('profile.cases.note')}</span>
                    <button
                        className={c.get}
                        onClick={handleBuyClick}
                        disabled={isLoading}
                    >
                        <span>
                            {isLoading
                                ? t('profile.cases.buying')
                                : t('profile.cases.buy')}
                        </span>
                    </button>
                </div>
            )
        }

        return (
            <div className={c.bottom}>
                <div className={c.code}>
                    <div className={c.codeRow}>
                        <span className={c.codeText}>{code}</span>
                        <span className={c.codeTitle}>
                            {t('profile.cases.promoCode')}
                        </span>
                    </div>
                    <button className={c.codeButton} onClick={handleCopyCode}>
                        <span>
                            {isCopied
                                ? t('profile.cases.copied')
                                : t('profile.cases.copy')}
                        </span>
                    </button>
                </div>
                <button className={c.get} onClick={handleGoToSite}>
                    <span>{t('profile.cases.getOnSite')}</span>
                </button>
            </div>
        )
    }

    return (
        <div
            className={c.component}
            style={{
                transform: `translateY(${translateY}px)`,
                transition: dragging.current ? 'none' : 'transform 0.3s ease',
            }}
        >
            <div
                className={c.trigger}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
            ></div>
            <div className={c.top}>
                <div className={c.item}>
                    <img
                        src={caseInfo.imageUrl || '/images/case-01.png'}
                        alt="Case"
                        className={c.casePic}
                    />
                    <div className={c.caseDetails}>
                        <span className={c.caseTitle}>{translatedCaseName}</span>
                        <span className={c.casePrice}>
                            {formatNumbers(caseInfo.price)}
                        </span>
                    </div>
                    <div className={c.limit}>
                        <span>{caseInfo?.codes?.available ?? 0}</span>/
                        {caseInfo?.codes?.total ?? 0}
                    </div>
                </div>
            </div>
            {getBottom()}
        </div>
    )
}
